import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { countryCodes } from '../mock';
import { toast } from 'sonner';
import { getGeoLocation, submitLead } from '../services/api';

export const PopupForm = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [geoData, setGeoData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    country: 'US',
    contactNumber: '',
    message: ''
  });

  // Fetch geolocation when form opens
  useEffect(() => {
    if (isOpen && !geoData) {
      fetchGeoLocation();
    }
  }, [isOpen]);

  const fetchGeoLocation = async () => {
    const result = await getGeoLocation();
    if (result.success) {
      setGeoData(result.data);
      // Auto-set country based on geolocation
      const countryCode = countryCodes.find(c => c.country === result.data.countryCode);
      if (countryCode) {
        setFormData(prev => ({ 
          ...prev, 
          countryCode: countryCode.code,
          country: result.data.countryCode 
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Extract dial code number from string (e.g., "+1" -> 1)
      const dialCode = parseInt(formData.countryCode.replace('+', ''));
      
      // Clean phone number - remove all non-numeric characters
      const cleanedPhone = formData.contactNumber.replace(/\D/g, '');
      
      // Validate phone number length (should be at least 7 digits)
      if (cleanedPhone.length < 7) {
        toast.error('Please enter a valid phone number (at least 7 digits)');
        setIsLoading(false);
        return;
      }

      const leadData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        countryDialCode: dialCode,
        country: formData.country,
        phoneNumber: cleanedPhone,
        description: formData.message.trim() || 'Contact form submission'
      };

      const result = await submitLead(leadData, geoData);

      if (result.success) {
        toast.success('Thank you! Your application has been submitted successfully.');
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          countryCode: '+1',
          country: 'US',
          contactNumber: '',
          message: ''
        });
        
        // Close modal
        onClose();
        
        // Redirect to thank you page with customer ID
        setTimeout(() => {
          navigate(result.redirectUrl);
        }, 1000);
      } else {
        // Show error message from API
        toast.error(result.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Get Started</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            disabled={isLoading}
          >
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
            <Input
              required
              disabled={isLoading}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="John Smith"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
            <Input
              type="email"
              required
              disabled={isLoading}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="john@company.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number *</label>
            <div className="flex gap-2">
              <Select
                value={formData.countryCode}
                onValueChange={(value) => {
                  const selected = countryCodes.find(c => c.code === value);
                  setFormData({ 
                    ...formData, 
                    countryCode: value,
                    country: selected?.country || 'US'
                  });
                }}
                disabled={isLoading}
              >
                <SelectTrigger className="w-28">
                  <SelectValue placeholder="+1" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {countryCodes.map((item) => (
                    <SelectItem key={item.country} value={item.code}>
                      {item.code} {item.country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="tel"
                required
                disabled={isLoading}
                value={formData.contactNumber}
                onChange={(e) => {
                  // Remove all non-numeric characters and limit to 15 digits
                  const value = e.target.value.replace(/\D/g, '').slice(0, 15);
                  setFormData({ ...formData, contactNumber: value });
                }}
                onKeyPress={(e) => {
                  // Prevent non-numeric key presses
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                placeholder="5551234567"
                maxLength={15}
                pattern="[0-9]*"
                inputMode="numeric"
                className="flex-1"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
            <Textarea
              disabled={isLoading}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="I need help registering my trademark for..."
              rows={4}
            />
          </div>

          <Button 
            type="submit" 
            className="w-full bg-primary hover:bg-primary/90"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Request'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};